import React, { useLayoutEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSeo } from "context/SeoContext";
import FaviconManager from "components/FaviconManager";

const MetaManager = (props) => {
  const defaultSeo = useSeo();
  const {
    title = defaultSeo.title,
    description = defaultSeo.description,
    keywords = defaultSeo.keywords,
    author = defaultSeo.author,
    canonical = defaultSeo.canonical,
    themeColor = defaultSeo.themeColor,
    
    // Open Graph Properties
    ogTitle = defaultSeo.ogTitle || title,
    ogDescription = defaultSeo.ogDescription || description,
    ogUrl = defaultSeo.ogUrl,
    ogImage = defaultSeo.ogImage,
    ogImageWidth = defaultSeo.ogImageWidth || "1200",
    ogImageHeight = defaultSeo.ogImageHeight || "630",
    ogSiteName = defaultSeo.ogSiteName || title,
    ogLocale = defaultSeo.ogLocale || "de_DE",
    
    // Twitter Card Properties
    twitterCard = defaultSeo.twitterCard,
    twitterTitle = defaultSeo.twitterTitle || title,
    twitterDescription = defaultSeo.twitterDescription || description,
    
    // Schema.org Properties
    schemaImage = defaultSeo.schemaImage || ogImage,
    
    // Favicons
    favicon32 = defaultSeo.favicon32,
    favicon16 = defaultSeo.favicon16,
  } = props;

  // Direct DOM manipulation to insert meta tags at the beginning of the head
  useLayoutEffect(() => {
    // A function that inserts meta tags directly into the head
    const injectMetaTags = () => {
      const head = document.head;
      
      // Remove all previous meta tags with data-injected-meta attribute
      const previousMetaTags = document.querySelectorAll('[data-injected-meta]');
      previousMetaTags.forEach(tag => tag.remove());
      
      // Define the meta tags to be inserted
      const metaTags = [
        { tag: 'meta', attrs: { charset: 'utf-8', 'data-injected-meta': 'true' } },
        { tag: 'title', content: title, attrs: { 'data-injected-meta': 'true' } },
        { tag: 'meta', attrs: { name: 'viewport', content: 'width=device-width, initial-scale=1', 'data-injected-meta': 'true' } },
        
        // Standard Meta Tags
        description ? { tag: 'meta', attrs: { name: 'description', content: description, 'data-injected-meta': 'true' } } : null,
        keywords ? { tag: 'meta', attrs: { name: 'keywords', content: keywords, 'data-injected-meta': 'true' } } : null,
        author ? { tag: 'meta', attrs: { name: 'author', content: author, 'data-injected-meta': 'true' } } : null,
        themeColor ? { tag: 'meta', attrs: { name: 'themeColor', content: themeColor, 'data-injected-meta': 'true' } } : null,
        canonical ? { tag: 'link', attrs: { rel: 'canonical', href: canonical, 'data-injected-meta': 'true' } } : null,
        
        // Schema.org for Google
        { tag: 'meta', attrs: { itemprop: 'name', content: title, 'data-injected-meta': 'true' } },
        description ? { tag: 'meta', attrs: { itemprop: 'description', content: description, 'data-injected-meta': 'true' } } : null,
        schemaImage ? { tag: 'meta', attrs: { itemprop: 'image', content: schemaImage, 'data-injected-meta': 'true' } } : null,
        
        // Open Graph Meta Tags
        ogTitle ? { tag: 'meta', attrs: { property: 'og:title', content: ogTitle, 'data-injected-meta': 'true' } } : null,
        ogDescription ? { tag: 'meta', attrs: { property: 'og:description', content: ogDescription, 'data-injected-meta': 'true' } } : null,
        ogImage ? { tag: 'meta', attrs: { property: 'og:image', content: ogImage, 'data-injected-meta': 'true' } } : null,
        ogImage ? { tag: 'meta', attrs: { property: 'og:image:width', content: ogImageWidth, 'data-injected-meta': 'true' } } : null,
        ogImage ? { tag: 'meta', attrs: { property: 'og:image:height', content: ogImageHeight, 'data-injected-meta': 'true' } } : null,
        ogUrl ? { tag: 'meta', attrs: { property: 'og:url', content: ogUrl, 'data-injected-meta': 'true' } } : null,
        { tag: 'meta', attrs: { property: 'og:site_name', content: ogSiteName, 'data-injected-meta': 'true' } },
        { tag: 'meta', attrs: { property: 'og:locale', content: ogLocale, 'data-injected-meta': 'true' } },
        { tag: 'meta', attrs: { property: 'og:type', content: 'website', 'data-injected-meta': 'true' } },
        
        // Twitter Card Meta Tags
        { tag: 'meta', attrs: { name: 'twitter:card', content: twitterCard, 'data-injected-meta': 'true' } },
        twitterTitle ? { tag: 'meta', attrs: { name: 'twitter:title', content: twitterTitle, 'data-injected-meta': 'true' } } : null,
        twitterDescription ? { tag: 'meta', attrs: { name: 'twitter:description', content: twitterDescription, 'data-injected-meta': 'true' } } : null,
        ogImage ? { tag: 'meta', attrs: { name: 'twitter:image', content: ogImage, 'data-injected-meta': 'true' } } : null,
      ].filter(Boolean); // Filter null entries
      
      // Find meta elements injected from header HTML to place our tags after them
      // Only look for meta/link elements, not scripts
      const headerMetaElements = document.querySelectorAll('[data-injected-header][data-injected-type="meta"]');
      const insertionPoint = headerMetaElements.length ? 
        headerMetaElements[headerMetaElements.length - 1].nextSibling : 
        head.firstChild;
      
      // Add the tags in reverse order to maintain correct order
      metaTags.reverse().forEach(tagInfo => {
        const element = document.createElement(tagInfo.tag);
        
        // Set attributes
        Object.entries(tagInfo.attrs || {}).forEach(([key, value]) => {
          element.setAttribute(key, value);
        });
        
        // Set content for title tag
        if (tagInfo.content && tagInfo.tag === 'title') {
          element.textContent = tagInfo.content;
        }
        
        // Insert the tag after header meta elements or at the beginning of head
        head.insertBefore(element, insertionPoint);
      });
    };
    
    // Execute the function immediately
    injectMetaTags();
    
    // Execute the function again after the page has fully loaded
    // to ensure the meta tags are in the right place
    const handleLoad = () => injectMetaTags();
    window.addEventListener('load', handleLoad);
    
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [title, description, keywords, author, themeColor, canonical, 
      ogTitle, ogDescription, ogUrl, ogImage, ogImageWidth, ogImageHeight, ogSiteName, ogLocale,
      twitterCard, twitterTitle, twitterDescription, schemaImage]);

  return (
    <>
      {/* We keep Helmet for browser tab title display */}
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <FaviconManager favicon16={favicon16} favicon32={favicon32} />
    </>
  );
};

export default MetaManager;
